import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconBuilder } from '@components/icons/builder';
import { IconEdit } from '@components/icons/edit';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRemove } from '@components/icons/remove';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { Table, TableProps } from '@components/table';
import { SimpleText } from '@components/table/renderers';
import { useTableRef } from '@components/table/table';
import { useTableStore } from '@components/table/table-store';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useMCPServers, useMCPServerJourneys, useDeleteMCPServerJourney } from 'api/mcp/hooks';
import { MCPServerJourney } from 'api/mcp/requests';

import { useMCPServerJourneySidebar } from './journey-sidebar';
import { useThirdPartyConnectionSidebar } from './third-party-connection-sidebar';

export const MCPServerDetail = () => {
  const { serverId } = useParams<{ serverId: string }>();
  const { t } = useScopedTranslate(locale);
  const { data: mcpServers } = useMCPServers();
  const { data: serverJourneysData = [], isLoading } = useMCPServerJourneys(serverId!);
  const deleteMCPServerJourneyMutation = useDeleteMCPServerJourney();
  const showDeleteModal = useDeleteModal();
  const showMCPServerJourneySidebar = useMCPServerJourneySidebar();
  const showThirdPartyConnectionSidebar = useThirdPartyConnectionSidebar();
  const tableRef = useTableRef();

  const server = mcpServers?.find((s) => s.clientId === serverId);

  const [tableSettings, setTableSettings] = useLocalStorageState({
    key: 'mcp-server-journeys-table-settings',
    defaultValue: {},
  });

  const tableStore = useTableStore({
    selectedView: tableSettings,
    onPersist: setTableSettings,
  });
  const availableColumns = useMemo(
    () => [
      {
        id: 'journeyTitle',
        title: t('journey'),
        type: 'text' as const,
        Cell: ({ row }: { row: MCPServerJourney }) => <SimpleText text={row.journeyTitle} />,
      },
      {
        id: 'description',
        title: t('description'),
        type: 'text' as const,
        Cell: ({ row }: { row: MCPServerJourney }) => <SimpleText text={row.description ?? ''} />,
      },
      {
        id: 'attributes',
        title: t('journeyDataFields'),
        type: 'number' as const,
        renderFallback: () => <Text font="regular14" text="2" />,
        Cell: ({ row }: { row: MCPServerJourney }) => (
          <Text font="regular14" text={((row.attributes?.length || 0) + 2).toString()} />
        ),
      },
    ],
    [t]
  );

  const endFixedColumns: TableProps<MCPServerJourney>['endFixedColumns'] = useMemo(
    () => [
      {
        id: 'options',
        title: '',
        type: 'custom' as const,
        Cell: ({ row }: { row: MCPServerJourney }) => (
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
            }}
          >
            <Menu.Item
              label={t('core.edit')}
              icon={<IconEdit />}
              onSelect={() =>
                showMCPServerJourneySidebar({
                  existingJourney: row,
                  serverId: serverId!,
                })
              }
            />
            <Menu.Item
              label={t('core.remove')}
              icon={<IconRemove />}
              variant="danger"
              onSelect={() =>
                showDeleteModal({
                  title: t('removeTitle'),
                  description: t('removeDescription'),
                  onConfirm: () => {
                    if (row.id && serverId) {
                      deleteMCPServerJourneyMutation.mutate({ serverId: serverId, id: row.id });
                    }
                  },
                })
              }
            />
          </Menu>
        ),
      },
    ],
    [t, serverId, showMCPServerJourneySidebar, showDeleteModal, deleteMCPServerJourneyMutation]
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!server) {
    return <div>{t('serverNotFound')}</div>;
  }

  return (
    <>
      <Header
        actions={
          <Stack orientation="horizontal" space={4}>
            <Button
              data-cy="connect-third-party-btn"
              text={t('connectToThirdPartyTools')}
              iconRight={<IconBuilder size={20} />}
              variant="secondary"
              onClick={() => server && showThirdPartyConnectionSidebar({ server })}
            />
            <Button
              data-cy="add-journey-btn"
              text={t('addJourney')}
              iconLeft={<IconAddPlus />}
              variant="primary"
              onClick={() => showMCPServerJourneySidebar({ serverId: serverId! })}
            />
          </Stack>
        }
        breadcrumbs={
          <Breadcrumb
            items={[
              { label: t('mcpServers'), link: '/settings/mcp-servers' },
              { label: server.label || server.clientId },
            ]}
          />
        }
      />
      <PageContent noPadding>
        <Table<MCPServerJourney>
          idColumn="id"
          availableColumns={availableColumns}
          data={serverJourneysData}
          endFixedColumns={endFixedColumns}
          store={tableStore}
          tableRef={tableRef}
          hideToolbar
          emptyState={
            <EmptyState
              skipMargin
              header={t('emptyStateHeader')}
              content={t('emptyStateContent')}
              icon={<IconJourneyItem />}
            />
          }
        />
      </PageContent>
    </>
  );
};

const locale = {
  en: {
    mcpServers: 'MCP Servers',
    journey: 'Journey',
    description: 'Description',
    journeyDataFields: 'Journey data fields',
    removeTitle: 'Remove Journey',
    removeDescription: 'Are you sure you want to remove this journey from this MCP server?',
    connectToThirdPartyTools: 'Connect to Third-Party Tools',
    addJourney: 'Add Journey',
    serverNotFound: 'Server not found',
    emptyStateHeader: 'No journeys added',
    emptyStateContent:
      'Add journeys to your MCP server and configure them. These journeys are then available to be triggered through your AI assistant.',
  },
};
